/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    shape, string, number,
    object,
    bool,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import loadable from '@loadable/component';

import { getPresentationFamily, getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getIsBot, getSSRDeviceType } from '../../../../state/ducks/App/App-Selectors';
import PanelT3 from '../PanelT3';
import uniqueId from '../../../helpers/contentstack/uniqueId';
import { emitCustomTrackingEvent } from '../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getCurrentPageView } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const HeroSingleDesktop = loadable(() => import(/* webpackChunkName: "HeroSingleDesktop" */  './Desktop/HeroSingleDesktop'));
const HeroSingleMobile = loadable(() => import(/* webpackChunkName: "HeroSingleMobile" */  './Mobile/HeroSingleMobile'));

const useStyles = makeStyles((theme) => ({
    foodBlock: {
        position: 'relative',
        margin: '0 auto 2rem',
        [theme.breakpoints.down(1024)]: {
            margin: '0 auto 1rem',
        },
    },
    foodBlockWithGrid: {
        position: 'relative',
        margin: '0 auto',
        width: '100%',
    },
    floralBlock: ({ height, pageView }) => ({
        [theme.breakpoints.up(1366)]: {
            width: '100%',
            height: `${height || 400}px`,
            margin: pageView?.pageType === 'home' ? 0 : '0 auto 2rem',
        },
    }),
    floralBlockWithGrid: ({ height, pageView }) => ({
        [theme.breakpoints.up(1366)]: {
            width: '100%',
            height: `${height || 400}px`,
            margin: pageView?.pageType === 'home' ? 0 : '0 auto',
        },
    }),
}));

const HeroSingleUI = ({
    data, block, salesforceResponse, isHeroRedesignEnabled,
}) => {
    const {
        presentation_type = '',
        height = '',
        reference = '',
        mobile_department_heading = '',
        unique_selector = '',
        device,
        tablet_x_offset = '',
        message_location = '',
        restrict_message_margins = false,
        mobile_height = '',
        block_margin = {},
    } = block;

    const {
        linking = {},
        is_mobile_copy = false,
        message_collection = [],
        message_styling = [],
        image = {},
        image_mobile = {},
        image_tablet = {},
        overlays = {},
        use_mobile_image_for_tablet = false,
        uid = '',
        container_attributes = [],
        image_opacity = null,
        message_positioning = null,
        video = {},
    } = reference[0];
    const pageView = useSelector(getCurrentPageView);
    const classes = useStyles({ height, pageView });
    const dispatch = useDispatch();
    const isBot = useSelector(getIsBot);
    useEffect(() => {
        if (!window.wuDepartmentPageHeroEventFired) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'Department Page Hero Redesign',
                eventLabel: isHeroRedesignEnabled ? 'Variant' : 'Control',
                feature_detail: isHeroRedesignEnabled ? 'Variant' : 'Control',
                feature_element: 'Department Page Hero Redesign',
                feature_category: 'Experiment',
            };
            dispatch(trackEvent(eventParam));
            window.wuDepartmentPageHeroEventFired = true;
        }
        return () => {
            window.wuDepartmentPageHeroEventFired = false;
        };
    }, []);

    const ffHasHomepageGridStructure = useSelector(getFeatureFlag('has-homepage-grid-structure'));

    const ssrDeviceType = useSelector(getSSRDeviceType);
    const presentationFamily = useSelector(getPresentationFamily);
    const dataUniqueId = uniqueId(unique_selector);
    if (data?.reference?.[0]?.reference?.[0]?._content_type_uid === 't3_panel') {
        return (
            <div className={classes.block} data-uniqueid={dataUniqueId} cmsuid={uid}>
                <PanelT3 data={data.reference[0]} />
            </div>
        );
    }
    const mobile_height_pixel = data?.mobile_height_pixel || '';

    const foodBlockCssStyle = ffHasHomepageGridStructure ? classes.foodBlockWithGrid : classes.foodBlock;
    const floralBlockCssStyle = ffHasHomepageGridStructure ? classes.floralBlockWithGrid : classes.floralBlock;
    const videoData = data?.reference?.[0]?.reference?.[0]?.video;
    const trackCustomClickstreamEventByType = (actionType = 'Impression') => {
        if (salesforceResponse?.campaign) {
            dispatch(emitCustomTrackingEvent({
                action: actionType,
                salesforceResponse,
                page: { type: 'home' },
                eventSubType: 'campaign-tracking',
            }));
        }
    };
    return (
        <div
            className={presentationFamily === 'food' ? `hero_block ${foodBlockCssStyle}` : `${floralBlockCssStyle}`}
            data-uniqueid={dataUniqueId}
            cmsuid={uid}
        >
            {ssrDeviceType === 'mobile'
                && (device === 'Both' || device === 'Mobile')
                && (
                    <HeroSingleMobile
                        data={data}
                        image_mobile={image_mobile}
                        message_collection={message_collection}
                        linking={linking}
                        mobile_department_heading={mobile_department_heading}
                        presentation_type={presentation_type}
                        mobile_height={mobile_height}
                        is_mobile_copy={is_mobile_copy}
                        container_attributes={container_attributes}
                        blockMargin={block_margin}
                        messagePositioning={message_positioning}
                        imageOpacity={image_opacity}
                        mobile_height_pixel={mobile_height_pixel}
                        videoPoster={data?.reference?.[0]?.reference?.[0]?.video_poster || {}}
                        videoData={videoData}
                        video={video}
                        trackCustomClickstreamEventByType={trackCustomClickstreamEventByType}
                        departmentPageHeroRedesign={isHeroRedesignEnabled}
                    />
                )}

            {ssrDeviceType !== 'mobile'
                && (device === 'Both' || device === 'Desktop')
                && (
                    <HeroSingleDesktop
                        linking={linking}
                        message_collection={message_collection}
                        message_styling={message_styling}
                        message_location={message_location}
                        image={image}
                        image_mobile={image_mobile} // optional for tablet
                        presentation_type={presentation_type}
                        height={height}
                        overlays={overlays}
                        isBot={isBot}
                        tabletbg={tablet_x_offset}
                        tabletMobImage={use_mobile_image_for_tablet}
                        restrict_message_margins={restrict_message_margins}
                        container_attributes={container_attributes}
                        blockMargin={block_margin}
                        imageOpacity={image_opacity}
                        messagePositioning={message_positioning}
                        isTabletImage={image_tablet}
                        videoPoster={data?.reference?.[0]?.reference?.[0]?.video_poster || {}}
                        videoData={videoData}
                        video={video}
                        trackCustomClickstreamEventByType={trackCustomClickstreamEventByType}
                        departmentPageHeroRedesign={isHeroRedesignEnabled}
                    />
                )}
        </div>
    );
};

HeroSingleUI.propTypes = {
    data: object.isRequired,
    salesforceResponse: object,
    isHeroRedesignEnabled: bool,
    block: object.isRequired,
};

HeroSingleUI.defaultProps = {
    salesforceResponse: {},
    isHeroRedesignEnabled: false,
};
export default HeroSingleUI;
